import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Text,
} from '@chakra-ui/react';
import { RiBuilding2Line, RiMailLine, RiTeamLine } from 'react-icons/ri';
import { HelivanColor } from '../helivan/HelivanColor';
import { OrganizationsList } from './OrganizationsList';
import { PendingInvitations } from './PendingInvitations';
import { OrganizationMembers } from './OrganizationMembers';
import { useGetPendingInvitationsQuery, useGetOrganizationWithMembersQuery } from '../../generated/graphql';
import { useAuth } from '../../contexts/AuthContext';

interface SectionHeaderProps {
  icon: any;
  title: string;
  badge?: number;
}

const SectionHeader = ({ icon, title, badge }: SectionHeaderProps) => (
  <HStack spacing={3} flex="1">
    <Flex
      w="28px"
      h="28px"
      borderRadius="6px"
      bg="rgba(0,71,125,0.08)"
      align="center"
      justify="center"
      flexShrink={0}
    >
      <Icon as={icon} boxSize="14px" color={HelivanColor.text.helivanBlue} />
    </Flex>
    <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
      {title}
    </Text>
    {badge !== undefined && badge > 0 && (
      <Badge
        colorScheme="orange"
        borderRadius="full"
        fontSize="10px"
        px={2}
      >
        {badge}
      </Badge>
    )}
  </HStack>
);

export const OrganizationSettings = () => {
  const { backendUser } = useAuth();
  const currentOrgId = backendUser?.currentOrganization?.id;

  const { data: invitationsData } = useGetPendingInvitationsQuery();
  const { data: orgData } = useGetOrganizationWithMembersQuery({
    variables: { organizationId: currentOrgId || '' },
    skip: !currentOrgId,
  });

  const pendingCount = invitationsData?.getPendingInvitations?.length || 0;
  const currentUserRole = orgData?.getOrganizationWithMembers?.currentUserRole;
  const canManageMembers = currentUserRole === 'OWNER' || currentUserRole === 'ADMIN';

  return (
    <Accordion defaultIndex={[0]} allowMultiple>
      {/* My Organizations */}
      <AccordionItem border="none" mb={3}>
        <AccordionButton
          bg="white"
          borderRadius="10px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          px={4}
          py={3}
          _hover={{ bg: HelivanColor.background.lightestGrey }}
          _expanded={{
            borderBottomRadius: 0,
            borderBottom: "none",
          }}
        >
          <SectionHeader icon={RiBuilding2Line} title="My Organizations" />
          <AccordionIcon color={HelivanColor.text.blueMid} />
        </AccordionButton>
        <AccordionPanel
          bg="white"
          borderRadius="0 0 10px 10px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          borderTop="none"
          p={4}
        >
          <OrganizationsList />
        </AccordionPanel>
      </AccordionItem>

      {/* Pending Invitations */}
      <AccordionItem border="none" mb={3}>
        <AccordionButton
          bg="white"
          borderRadius="10px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          px={4}
          py={3}
          _hover={{ bg: HelivanColor.background.lightestGrey }}
          _expanded={{
            borderBottomRadius: 0,
            borderBottom: "none",
          }}
        >
          <SectionHeader icon={RiMailLine} title="Pending Invitations" badge={pendingCount} />
          <AccordionIcon color={HelivanColor.text.blueMid} />
        </AccordionButton>
        <AccordionPanel
          bg="white"
          borderRadius="0 0 10px 10px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          borderTop="none"
          p={4}
        >
          <PendingInvitations />
        </AccordionPanel>
      </AccordionItem>

      {/* Manage Members - Only visible to Owners and Admins */}
      {canManageMembers && (
        <AccordionItem border="none">
          <AccordionButton
            bg="white"
            borderRadius="10px"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
            px={4}
            py={3}
            _hover={{ bg: HelivanColor.background.lightestGrey }}
            _expanded={{
              borderBottomRadius: 0,
              borderBottom: "none",
            }}
          >
            <SectionHeader icon={RiTeamLine} title="Manage Members" />
            <AccordionIcon color={HelivanColor.text.blueMid} />
          </AccordionButton>
          <AccordionPanel
            bg="white"
            borderRadius="0 0 10px 10px"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
            borderTop="none"
            p={4}
          >
            <OrganizationMembers />
          </AccordionPanel>
        </AccordionItem>
      )}
    </Accordion>
  );
};
